import { createAsyncThunk } from '@reduxjs/toolkit'

import type { RootState } from 'redux/store/RootReducer'
import type { AppDispatch } from 'redux/store'
import brainstormerAPI from 'api/ApiActions/Brainstormer/Action'
import { getToolAdapter } from 'containers/ToolBuilder/adapters'

const searchParams = new URLSearchParams(window.location.search)
const toolId = searchParams.get('tool_id') as string
const projectId = searchParams.get('project_id') as string
const toolType = searchParams.get('tool_type') as 'Brainstormer'

const toolAPI = {
  Brainstormer: brainstormerAPI,
}

const API = toolAPI[toolType]

export const fetchTool: any = createAsyncThunk<
  any,
  any,
  {
    dispatch: AppDispatch
    state: RootState
  }
>('toolBuilder/fetchTool', async (_obj, { rejectWithValue }) => {
  try {
    const adapter = getToolAdapter()
    const response = await API.get(projectId, toolId)
    return adapter.parseToAppState({
      ...response?.data?.data?.attributes,
      id: response?.data?.data?.id,
    })
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const saveTool: any = createAsyncThunk<
  any,
  any,
  {
    dispatch: AppDispatch
    state: RootState
  }
>('toolBuilder/saveTool', async (_obj, { getState, rejectWithValue }) => {
  try {
    const adapter = getToolAdapter()
    const { toolBuilder } = getState().toolBuilder
    const requestPayload = adapter.parseToAPI(toolBuilder)

    const response = await API.create(requestPayload, projectId)
    return adapter.parseToAppState({
      ...response?.data?.data?.attributes,
      id: response?.data?.data?.id,
    })
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const updateTool: any = createAsyncThunk<
  any,
  any,
  {
    dispatch: AppDispatch
    state: RootState
  }
>('toolBuilder/updateTool', async (_obj, { getState, rejectWithValue }) => {
  try {
    const { toolBuilder } = getState().toolBuilder
    const adapter = getToolAdapter()
    const requestPayload = adapter.parseToAPI(toolBuilder)

    const response = await API.update(
      requestPayload,
      projectId,
      toolBuilder?.id ?? toolId,
    )
    return adapter.parseToAppState({
      ...response?.data?.data?.attributes,
      id: response?.data?.data?.id,
    })
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const updateToolState: any = createAsyncThunk<
  any,
  any,
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  'toolBuilder/updateToolState',
  async (
    { state }: { state: 'publish' | 'unpublish' | 'archive' | 'unarchive' },
    { getState, rejectWithValue },
  ) => {
    try {
      const { id } = getState().toolBuilder.toolBuilder

      const adapter = getToolAdapter()
      const response = await API.updateState(state, projectId, id ?? toolId)

      return adapter.parseToAppState({
        ...response?.data?.data?.attributes,
        id: response?.data?.data?.id,
      })
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const generatePermaLink: any = createAsyncThunk<
  any,
  any,
  {
    dispatch: AppDispatch
    state: RootState
  }
>('toolBuilder/generatePermaLink', async ({ value }, { rejectWithValue }) => {
  try {
    const response = await API.generatePermalink(projectId, { name: value })
    if (response?.status >= 200 && response?.status < 300) {
      return response?.data?.data?.attributes?.permalink
    }
    return rejectWithValue('Error while generating permalink')
  } catch (error) {
    return rejectWithValue(error)
  }
})
export const fetchAdministratorEmails: any = createAsyncThunk<
  any,
  any,
  {
    dispatch: AppDispatch
    state: RootState
  }
>('toolBuilder/fetchAdministratorEmails', async (_obj, { rejectWithValue }) => {
  try {
    const response = await API.getAdministratorEmails()
    if (response?.status >= 200 && response?.status < 300) {
      return response?.data?.data?.map(
        (users: { attributes: { email: string } }) => users?.attributes?.email,
      )
    }
    return rejectWithValue('Error while fetching administrator emails')
  } catch (error) {
    return rejectWithValue(error)
  }
})
